import {Button, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {StyledGrid, TagChip} from 'presentation/components';
import {FeaturesEnum, PlanTypeEnum, UserTypeEnum} from 'presentation/constants';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private';
import {translations} from 'presentation/translations-new';

import {ChangePlanDialog} from 'presentation/components/ChangePlanDialog';
import {FullscreenLoading} from 'presentation/components/Loading/FullscreenLoading';
import {Loading} from 'presentation/components/Loading/Loading';
import {planTypeOptions} from '../../constants';
import {InactiveBeneficiariesAccordion} from './components/BeneficiariesSection/InactiveBeneficiariesAccordion';
import {RenderBeneficiaries} from './components/BeneficiariesSection/RenderBeneficiaries';
import {useBeneficiarySelection} from './hooks/useBeneficiarySelection';
import {SubHeaderContainer} from './styles';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {formatText} from 'presentation/utils';
import {BeneficiarySituationEnum} from 'domain/usecases/others/selection-flow/beneficiary-v3';

const {changePlan, plan, loading, responsibleWithoutBenefits} = translations['pt-br'].commons;

function BeneficiarySelection() {
	const {isTablet} = useMediaQueries();
	const {palette, colors} = useTheme();

	const {isEnabled: isNewUIEntryFlowIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_NEW_UI_ENTRY_FLOW,
	);

	const {handleLogout} = useAccountInformation();
	const {
		beneficiaryListIsLoading,
		isSubmitting,
		beneficiariesList,
		selectedPlan,
		handleGoToPlanSelection,
		isCompareSelectedBeneficiary,
		handleSelectBeneficiary,
		isPlanCancelledDatesAreEqual,
		showChangePlanDialogState,
		setShowChangePlanDialogState,
		sections,
	} = useBeneficiarySelection();

	if (beneficiaryListIsLoading) {
		return <Loading text={loading} boxProps={{flex: 1}} />;
	}

	if (isSubmitting) {
		return <FullscreenLoading text={loading} />;
	}
	const shouldLogout =
		!beneficiaryListIsLoading && (!beneficiariesList || beneficiariesList?.length === 0);

	if (shouldLogout) {
		return handleLogout();
	}

	if (!beneficiariesList) return null;

	const allBeneficiaries = sections?.flatMap(section => section.items) ?? [];
	const activeBeneficiaries = allBeneficiaries.filter(
		b => b?.situacao !== BeneficiarySituationEnum.CANCELLED,
	);
	const canceledBeneficiaries = allBeneficiaries.filter(
		b => b?.situacao === BeneficiarySituationEnum.CANCELLED,
	);
	const hasMixed = activeBeneficiaries.length > 0 && canceledBeneficiaries.length > 0;
	const hasOnlyActive = canceledBeneficiaries.length === 0;
	const hasOnlyCanceled = activeBeneficiaries.length === 0 && canceledBeneficiaries.length > 0;

	return (
		<Fragment>
			{isTablet ? (
				<SubHeaderContainer>
					<Stack gap={1}>
						<Typography variant="body2" fontWeight={600} color={palette.text.secondary}>
							{plan}
						</Typography>
						<Typography color={palette.text.primary}>
							{selectedPlan?.nmPlano}
						</Typography>
					</Stack>
					<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
						{selectedPlan ? (
							<TagChip
								label={planTypeOptions[selectedPlan?.tipoPlanoC]?.label}
								color={planTypeOptions[selectedPlan?.tipoPlanoC]?.color}
								bgColor={
									selectedPlan?.tipoPlanoC === PlanTypeEnum.HEALTH
										? colors.light.primary['30Percent']
										: undefined
								}
							/>
						) : null}
						<Button onClick={handleGoToPlanSelection} variant="text">
							{changePlan}
						</Button>
					</Stack>
				</SubHeaderContainer>
			) : null}
			<Stack padding={isTablet ? 2 : 4} gap={3} paddingBottom={3} flex={1}>
				{!isNewUIEntryFlowIsEnabled
					? sections?.map(({title, items}) => {
							const isResponsibleWithoutBenefits =
								items?.some(
									beneficiary =>
										beneficiary?.tipoUsuarioC === UserTypeEnum.HOLDER &&
										beneficiary?.flTitularSemDireito,
								) ?? false;

							return (
								<Stack key={title}>
									<Stack mb={2}>
										<Typography
											variant="subtitle2"
											lineHeight={'16px'}
											color={palette.text.secondary}>
											{formatText(title)}
										</Typography>
										{isResponsibleWithoutBenefits ? (
											<Typography
												variant="subtitle2"
												lineHeight={'16px'}
												fontSize={12}
												color={palette.text.secondary}>
												{responsibleWithoutBenefits}
											</Typography>
										) : null}
									</Stack>

									<StyledGrid.Root rowSpacing={2} spacing={isTablet ? 1 : 3}>
										<RenderBeneficiaries
											list={items}
											isCompareSelectedBeneficiary={
												isCompareSelectedBeneficiary
											}
											handleSelectBeneficiary={handleSelectBeneficiary}
											hasOnlyCanceled={
												isPlanCancelledDatesAreEqual || hasOnlyCanceled
											}
										/>
									</StyledGrid.Root>
								</Stack>
							);
						})
					: null}

				{isNewUIEntryFlowIsEnabled ? (
					<StyledGrid.Root spacing={isTablet ? 2 : 3}>
						{isPlanCancelledDatesAreEqual || hasOnlyCanceled ? (
							<InactiveBeneficiariesAccordion
								list={canceledBeneficiaries}
								isCompareSelectedBeneficiary={isCompareSelectedBeneficiary}
								handleSelectBeneficiary={handleSelectBeneficiary}
								hasOnlyCanceled={isPlanCancelledDatesAreEqual || hasOnlyCanceled}
							/>
						) : null}

						{hasMixed ? (
							<Fragment>
								<RenderBeneficiaries
									list={activeBeneficiaries}
									isCompareSelectedBeneficiary={isCompareSelectedBeneficiary}
									handleSelectBeneficiary={handleSelectBeneficiary}
									hasOnlyCanceled={isPlanCancelledDatesAreEqual}
								/>

								<InactiveBeneficiariesAccordion
									list={canceledBeneficiaries}
									isCompareSelectedBeneficiary={isCompareSelectedBeneficiary}
									handleSelectBeneficiary={handleSelectBeneficiary}
									hasOnlyCanceled={isPlanCancelledDatesAreEqual}
								/>
							</Fragment>
						) : null}

						{hasOnlyActive ? (
							<RenderBeneficiaries
								list={activeBeneficiaries}
								isCompareSelectedBeneficiary={isCompareSelectedBeneficiary}
								handleSelectBeneficiary={handleSelectBeneficiary}
								hasOnlyCanceled={isPlanCancelledDatesAreEqual}
							/>
						) : null}
					</StyledGrid.Root>
				) : null}
			</Stack>
			<ChangePlanDialog
				open={showChangePlanDialogState}
				setOpen={setShowChangePlanDialogState}
			/>
		</Fragment>
	);
}

export {BeneficiarySelection};
